import { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import FormComp from "../../../Componentes/FormComp";
import { useModal } from "../../../contexts/ModalContext";
import { useAmbienteMedicao } from "../../../contexts/AmbienteMedicaoContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";
import { formatBRLFromCents, parseCentsFromInput } from "../../../Funcoes/Money";

const DISCIPLINAS = [
    "ELÉTRICA",
    "INSTRUMENTAÇÃO",
    "ESTRUTURA METÁLICA",
    "ISOLAMENTO TÉRMICO",
    "PARAFUSOS E FIXAÇÃO",
    "TUBULAÇÃO",
    "LOCAÇÃO",
    "OUTRO",
];

const toCaixaAlta = (str) => (typeof str === "string" ? str.toLocaleUpperCase("pt-BR") : str);

const ResumoLinha = styled.div`
    background-color: #f5f5f5;
    border-radius: 10px;
    padding: 12px 14px;
    margin-bottom: 16px;
    font-size: 14px;
    color: #333;
    line-height: 1.5;
    p {
        margin: 4px 0;
    }
`;

const initialForm = (fat) => {

    const d = toCaixaAlta((fat?.disciplina ?? "").trim());
    const inList = DISCIPLINAS.includes(d);

    return {
        disciplina: inList ? d : "OUTRO",
        disciplinaOutro: inList ? "" : d,
        numPedidoFornecedor: toCaixaAlta((fat?.num_pedido_fornec ?? "").trim()),
        nomeFornecedor: toCaixaAlta((fat?.fornecedor ?? "").trim()),
        valorPedido: Number(fat?.valor) || 0,
    };

};

const ModalEditarPCFaturamentoDireto = ({ faturamento, onFechar, onSalvo }) => {

    const { id: idPc } = useParams();
    const { closeModal } = useModal();
    const { ambiente } = useAmbienteMedicao();
    const { notifySuccess, notifyError } = useToast();
    const api = useApi();

    const [saving, setSaving] = useState(false);
    const [formValues, setFormValues] = useState(() => initialForm(faturamento));

    useEffect(() => {

        setFormValues(initialForm(faturamento));

    }, [faturamento?.id]);

    const handleChange = (e) => {

        const { name, value } = e.target;

        if (name === "valorPedido") {
            setFormValues((prev) => ({ ...prev, [name]: parseCentsFromInput(value) }));
            return;
        }

        if (name === "disciplina") {
            const v = toCaixaAlta(value);
            setFormValues((prev) => ({
                ...prev,
                disciplina: v,
                disciplinaOutro: v === "OUTRO" ? prev.disciplinaOutro : "",
            }));
            return;
        }

        setFormValues((prev) => ({
            ...prev,
            [name]: typeof value === "string" ? toCaixaAlta(value) : value,
        }));

    };

    const disciplinaEhOutro = formValues.disciplina === "OUTRO";

    const fechar = () => {

        closeModal();
        if (typeof onFechar === "function") {
            onFechar();
        }

    };

    const handleSalvar = async (e) => {

        e.preventDefault();

        const disciplinaFinal = disciplinaEhOutro
            ? toCaixaAlta(formValues.disciplinaOutro.trim())
            : formValues.disciplina;

        if (!formValues.disciplina) {
            notifyError("Selecione a disciplina.");
            return;
        }

        if (disciplinaEhOutro && !formValues.disciplinaOutro.trim()) {
            notifyError("Informe a disciplina quando a opção for OUTRO.");
            return;
        }

        if (!disciplinaFinal) {
            notifyError("Disciplina inválida.");
            return;
        }

        if (!formValues.numPedidoFornecedor.trim()) {
            notifyError("Informe o Nº pedido / fornecedor.");
            return;
        }

        if (!formValues.nomeFornecedor.trim()) {
            notifyError("Informe o fornecedor.");
            return;
        }

        if (formValues.valorPedido <= 0) {
            notifyError("O valor do pedido deve ser maior que zero.");
            return;
        }

        const somaNf = Number(faturamento?.soma_nf) || 0;
        if (formValues.valorPedido < somaNf) {
            notifyError(
                `O valor não pode ser menor que a soma das NFs (${formatBRLFromCents(somaNf)}).`
            );
            return;
        }

        setSaving(true);

        try {

            const formData = new FormData();
            formData.append("id", String(faturamento.id));
            formData.append("id_pc", String(idPc));
            formData.append("disciplina", disciplinaFinal);
            formData.append("num_pedido_fornec", formValues.numPedidoFornecedor.trim());
            formData.append("fornecedor", formValues.nomeFornecedor.trim());
            formData.append("valor", String(formValues.valorPedido));

            await api.post("/Painel/API/ControleMedicao/UpdatePcFaturamentoDireto/", formData);

            notifySuccess("Registro atualizado com sucesso.");
            fechar();
            if (typeof onSalvo === "function") {
                await onSalvo();
            }

        } catch (err) {

            const msg = err?.response?.data?.message || "Erro ao atualizar. Tente novamente.";
            notifyError(typeof msg === "string" ? msg : "Erro ao atualizar. Tente novamente.");
            console.error(err);

        } finally {

            setSaving(false);

        }

    };

    return (
        <Overlay state={true}>
            <Modal header close={fechar} size="md">
                <ResumoLinha>
                    <p>
                        <b>EDITAR FATURAMENTO DIRETO</b>
                    </p>
                    <p>
                        <b>OBRA:</b> {toCaixaAlta(ambiente?.descricao ?? "—")}
                    </p>
                    <p>
                        <b>Soma NFs nesta linha:</b>{" "}
                        {formatBRLFromCents(Number(faturamento?.soma_nf) || 0)}
                    </p>
                </ResumoLinha>
                <Formulario onSubmit={handleSalvar}>
                    <FormComp
                        title="DISCIPLINA"
                        type="select"
                        options={DISCIPLINAS}
                        valor={formValues.disciplina}
                        name="disciplina"
                        changeFunc={handleChange}
                        required
                    />
                    {disciplinaEhOutro && (
                        <FormComp
                            title="ESPECIFIQUE A DISCIPLINA"
                            type="text"
                            valor={formValues.disciplinaOutro}
                            name="disciplinaOutro"
                            changeFunc={handleChange}
                            required
                        />
                    )}
                    <FormComp
                        title="N PEDIDO / FORNECEDOR"
                        type="text"
                        valor={formValues.numPedidoFornecedor}
                        name="numPedidoFornecedor"
                        changeFunc={handleChange}
                        required
                    />
                    <FormComp
                        title="NOME FORNECEDOR"
                        type="text"
                        valor={formValues.nomeFornecedor}
                        name="nomeFornecedor"
                        changeFunc={handleChange}
                        required
                    />
                    <FormComp
                        title="VALOR DO PEDIDO"
                        type="text"
                        inputMode="numeric"
                        placeholder="R$ 0,00"
                        valor={formatBRLFromCents(formValues.valorPedido)}
                        name="valorPedido"
                        changeFunc={handleChange}
                        required
                    />
                    <FormComp
                        type="submit"
                        valor={saving ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
                        disabled={saving}
                    />
                </Formulario>
            </Modal>
        </Overlay>
    );

};

export default ModalEditarPCFaturamentoDireto;
